import { Button, SimpleProgressBar } from '@dixa/lib-shared-habla';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { shallowEqual } from 'react-redux';

import { DixaLogo } from 'components/dixaLogo';
import { useAppSelector } from 'utils/redux/useAppSelector';

import styles from './appStatus.module.scss';
import { AppErrorMessageAction, appErrors } from './utils/appErrors';

const loadingStatusMessage = 'Loading...';
const loadingLongStatusMessage = 'This is taking longer than usual...';

/**
 * The delay in milliseconds before the `loading longer than usual` status
 * message gets displayed.
 */
const loadingLongDelay = 10000; // 10s.

/** A component that displays the app status screen. */
export const AppStatus = (): JSX.Element => {
  const timeoutHandle = useRef<number>();
  const [progressBarFillScaleX, setProgressBarFillScaleX] = useState(0);
  const [pendingLong, setPendingLong] = useState<boolean>(false);
  const { error, appAsyncStatus, progress } = useAppSelector(
    (state) => ({
      appAsyncStatus: state.app.asyncStatus.app,
      error: state.app.error,
      progress: {
        presence: state.app.asyncStatus.presence,
        featureFlags: state.app.asyncStatus.featureFlags,
        webSocket: state.websocket.meta.asyncCreateStatus,
      },
    }),
    shallowEqual,
  );

  // Governs the timer for when the app is pending too long.
  useEffect(() => {
    if (appAsyncStatus === 'pending' && !timeoutHandle.current) {
      timeoutHandle.current = window.setTimeout(
        () => setPendingLong(true),
        loadingLongDelay,
      );
    } else {
      window.clearTimeout(timeoutHandle.current);
    }

    return () => window.clearTimeout(timeoutHandle.current);
  }, [appAsyncStatus]);

  // Advances the progress bar as progress members are fulfilling.
  useEffect(() => {
    if (error) {
      setProgressBarFillScaleX(0);
    } else {
      const totalProgress = Object.keys(progress).length;
      const currentProgress = Object.keys(progress).filter(
        (progressKey) => progress[progressKey] === 'fulfilled',
      ).length;

      setProgressBarFillScaleX(
        // We +1 to ensure the progress appears to have advanced in the start.
        (currentProgress + 1) / (totalProgress + 1),
      );
    }
  }, [progress, error]);

  /** Renders the progress status. */
  const progressStatus = useMemo(() => {
    if (!error) {
      return (
        <div className={styles.progressStatus}>
          <SimpleProgressBar progress={progressBarFillScaleX * 100} />

          <div className={styles.status}>
            {pendingLong ? loadingLongStatusMessage : loadingStatusMessage}
          </div>
        </div>
      );
    }

    return undefined;
  }, [error, progressBarFillScaleX, pendingLong]);

  /** Handles the error action button. */
  const handleErrorAction = useCallback(
    (
      action: AppErrorMessageAction,
      event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    ) => {
      if (action.type === 'link') {
        window.open(action.url, '_blank', 'noreferrer');
      } else {
        action.onClick(event);
      }
    },
    [],
  );

  /** Renders the error message. */
  const errorMessage = useMemo(() => {
    if (error) {
      const { title, description, actions } = appErrors[error.type];

      const actionElements = !!actions?.length && (
        <div className={styles.errorActions}>
          {actions.map((action, index) => (
            <Button
              key={index}
              onClick={(event) => handleErrorAction(action, event)}
            >
              {action.label}
            </Button>
          ))}
        </div>
      );

      return (
        <div className={styles.error}>
          <div className={styles.errorTitle}>{title}</div>
          <div className={styles.errorDescription}>{description}</div>
          {actionElements}
        </div>
      );
    }

    return undefined;
  }, [error, handleErrorAction]);

  return (
    <div className={styles.root}>
      <div className={styles.container}>
        <DixaLogo className={styles.logo} />

        {progressStatus}
        {errorMessage}
      </div>
    </div>
  );
};
